/**
 * Full-screen dialog for adding or editing a customization field.
 * Two-column layout: field type selection on the left, settings on the right.
 *
 * In add mode: form resets after success, dialog stays open (rapid sequential).
 * In edit mode: dialog closes after save.
 *
 * Plan: 126-template-detail-page (redesigned from sidebar sheet to full-screen dialog)
 */

import { useState, useCallback, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import {
  ChevronDown,
  Type,
  FileText,
  List,
  CheckSquare,
  ToggleLeft,
  Paperclip,
  X,
  Plus,
  Trash2,
  ImagePlus,
  Loader2,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { useLanguage } from "@/i18n/useLanguage";
import { uploadFile } from "@/lib/erp/files";
import type { TemplateField, FieldType } from "../api/customizationAdminTypes";
import type { LucideIcon } from "lucide-react";

// ---------------------------------------------------------------------------
// i18n
// ---------------------------------------------------------------------------

const i18n = {
  addField: { en: "Add Field", th: "เพิ่มฟิลด์" },
  editField: { en: "Edit Field", th: "แก้ไขฟิลด์" },
  addDesc: { en: "Choose a field type and configure its settings.", th: "เลือกประเภทฟิลด์และตั้งค่า" },
  editDesc: { en: "Update this field's settings.", th: "อัปเดตการตั้งค่าฟิลด์นี้" },
  fieldType: { en: "Field Type", th: "ประเภทฟิลด์" },
  settings: { en: "Settings", th: "การตั้งค่า" },
  label: { en: "Label", th: "ป้ายชื่อ" },
  labelPlaceholder: { en: "e.g., Name Text", th: "เช่น ข้อความชื่อ" },
  required: { en: "Required", th: "จำเป็น" },
  requiredHelp: { en: "Customer must fill in this field", th: "ลูกค้าต้องกรอกฟิลด์นี้" },
  maxChars: { en: "Max Characters", th: "จำนวนตัวอักษรสูงสุด" },
  noLimit: { en: "0 = no limit", th: "0 = ไม่จำกัด" },
  options: { en: "Options", th: "ตัวเลือก" },
  optionPlaceholder: { en: "Option name", th: "ชื่อตัวเลือก" },
  addOption: { en: "Add Option", th: "เพิ่มตัวเลือก" },
  optionImageTooltip: { en: "Upload option image (shown as a thumbnail)", th: "อัปโหลดรูปตัวเลือก (แสดงเป็นรูปย่อ)" },
  allowedFileTypes: { en: "Allowed File Types", th: "ประเภทไฟล์ที่อนุญาต" },
  maxFileSize: { en: "Max File Size (MB)", th: "ขนาดไฟล์สูงสุด (MB)" },
  addonPricing: { en: "Add-on Pricing", th: "ราคาเสริม" },
  addonPrice: { en: "Extra Charge (฿)", th: "ค่าเสริม (฿)" },
  addonPriceHelp: { en: "Amount to charge when this field is filled.", th: "จำนวนเงินที่เรียกเก็บเมื่อกรอกฟิลด์นี้" },
  addonItemCode: { en: "Linked Item (optional)", th: "สินค้าที่เชื่อม (ไม่บังคับ)" },
  addonPlaceholder: { en: "e.g., EMBROIDERY-SERVICE", th: "เช่น EMBROIDERY-SERVICE" },
  addonHelp: { en: "Optionally link a service Item for inventory tracking.", th: "เชื่อมสินค้าบริการเพื่อติดตามสต็อก (ไม่บังคับ)" },
  cancel: { en: "Cancel", th: "ยกเลิก" },
  saveChanges: { en: "Save Changes", th: "บันทึกการเปลี่ยนแปลง" },
} as const;

// ---------------------------------------------------------------------------
// Field type cards (bigger, more descriptive for full-screen layout)
// ---------------------------------------------------------------------------

interface FieldTypeInfo {
  type: FieldType;
  label: { en: string; th: string };
  description: { en: string; th: string };
  icon: LucideIcon;
}

const FIELD_TYPES: FieldTypeInfo[] = [
  {
    type: "Text Input",
    label: { en: "Text Input", th: "ข้อความสั้น" },
    description: { en: "Single line of text", th: "ข้อความบรรทัดเดียว" },
    icon: Type,
  },
  {
    type: "Text Area",
    label: { en: "Text Area", th: "ข้อความยาว" },
    description: { en: "Multiple lines of text", th: "ข้อความหลายบรรทัด" },
    icon: FileText,
  },
  {
    type: "Single Select",
    label: { en: "Single Select", th: "เลือกรายการเดียว" },
    description: { en: "Pick one from a list", th: "เลือกหนึ่งจากรายการ" },
    icon: List,
  },
  {
    type: "Multi Select",
    label: { en: "Multi Select", th: "เลือกหลายรายการ" },
    description: { en: "Pick multiple from a list", th: "เลือกหลายรายการจากรายการ" },
    icon: CheckSquare,
  },
  {
    type: "Toggle",
    label: { en: "Toggle", th: "สวิตช์" },
    description: { en: "Yes or No switch", th: "เปิด/ปิด" },
    icon: ToggleLeft,
  },
  {
    type: "File Upload",
    label: { en: "File Upload", th: "อัปโหลดไฟล์" },
    description: { en: "Upload images or documents", th: "อัปโหลดรูปภาพหรือเอกสาร" },
    icon: Paperclip,
  },
];

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface FieldSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  editField?: TemplateField | null;
  onSave: (field: Omit<TemplateField, "name" | "sort_order">) => void;
}

const EMPTY_FIELD: Omit<TemplateField, "name" | "sort_order"> = {
  label: "",
  field_type: "Text Input",
  required: 0,
  options: "",
  option_images: "",
  addon_item: "",
  addon_price: 0,
  max_characters: 0,
  allowed_file_types: "",
  max_file_size: 5,
};

/** Parse the option_images JSON map; tolerant of empty/invalid. */
function parseOptionImages(raw: string | undefined): Record<string, string> {
  if (!raw) return {};
  try {
    const o = JSON.parse(raw);
    return o && typeof o === "object" ? (o as Record<string, string>) : {};
  } catch {
    return {};
  }
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function FieldSheet({ open, onOpenChange, editField, onSave }: FieldSheetProps) {
  const { language } = useLanguage();
  const t = (key: keyof typeof i18n) => i18n[key][language] || i18n[key].en;

  const isEdit = !!editField;
  const [form, setForm] = useState(EMPTY_FIELD);
  const [addonOpen, setAddonOpen] = useState(false);

  useEffect(() => {
    if (open) {
      if (editField) {
        setForm({
          label: editField.label,
          field_type: editField.field_type,
          required: editField.required,
          options: editField.options,
          option_images: editField.option_images ?? "",
          addon_item: editField.addon_item,
          addon_price: editField.addon_price,
          max_characters: editField.max_characters,
          allowed_file_types: editField.allowed_file_types,
          max_file_size: editField.max_file_size,
        });
        if (editField.addon_item || editField.addon_price > 0) setAddonOpen(true);
      } else {
        setForm(EMPTY_FIELD);
        setAddonOpen(false);
      }
    }
  }, [open, editField]);

  const handleSave = useCallback(() => {
    if (!form.label.trim()) return;
    onSave({ ...form, label: form.label.trim() });

    if (!isEdit) {
      setForm(EMPTY_FIELD);
      setAddonOpen(false);
    }
  }, [form, isEdit, onSave]);

  const updateField = <K extends keyof typeof form>(key: K, value: (typeof form)[K]) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  // Per-option artwork thumbnails (TKT-126). option_images is a JSON map
  // keyed by the option value; the storefront matches by that value.
  const [uploadingOpt, setUploadingOpt] = useState<string | null>(null);
  const optionImages = parseOptionImages(form.option_images);

  const setOptionImage = (opt: string, url: string | null) => {
    const next = { ...parseOptionImages(form.option_images) };
    if (url) next[opt] = url;
    else delete next[opt];
    updateField("option_images", Object.keys(next).length ? JSON.stringify(next) : "");
  };

  // Keep an option's image bound to its text if the staff rename it.
  const renameOptionImageKey = (oldOpt: string, newOpt: string) => {
    if (oldOpt === newOpt) return;
    const map = parseOptionImages(form.option_images);
    if (map[oldOpt] === undefined) return;
    const url = map[oldOpt];
    delete map[oldOpt];
    if (newOpt.trim()) map[newOpt] = url;
    updateField("option_images", Object.keys(map).length ? JSON.stringify(map) : "");
  };

  const handleOptionImageUpload = useCallback(
    async (opt: string, file: File) => {
      if (!file) return;
      setUploadingOpt(opt);
      try {
        // Public upload (is_private=0) -> absolute cdn.zaviago.com URL so the
        // storefront picker can render it directly.
        const { file_url } = await uploadFile(file, { isPrivate: false });
        setOptionImage(opt, file_url);
      } catch (e) {
        console.error("[FieldSheet] option image upload failed", e);
      } finally {
        setUploadingOpt(null);
      }
    },
    // setOptionImage reads latest form via parse; include form.option_images
    [form.option_images] // eslint-disable-line react-hooks/exhaustive-deps
  );

  const showMaxChars = form.field_type === "Text Input" || form.field_type === "Text Area";
  const showOptions = form.field_type === "Single Select" || form.field_type === "Multi Select";
  const showFileSettings = form.field_type === "File Upload";

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-6xl w-[95vw] max-h-[92vh] flex flex-col p-0 [&>button.absolute]:hidden">
        {/* Header — custom close button on the right */}
        <div className="flex items-start justify-between px-8 pt-8 pb-4">
          <div>
            <DialogTitle className="text-xl font-semibold">
              {isEdit ? t("editField") : t("addField")}
            </DialogTitle>
            <DialogDescription className="mt-1">
              {isEdit ? t("editDesc") : t("addDesc")}
            </DialogDescription>
          </div>
          <button
            type="button"
            onClick={() => onOpenChange(false)}
            className="shrink-0 ml-4 mt-0.5 rounded-full p-2 text-muted-foreground hover:bg-accent hover:text-foreground transition-colors"
            aria-label="Close"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Body — two columns (min-h-0 required for flex child to shrink + scroll) */}
        <div className="flex-1 min-h-0 overflow-y-auto px-8 pb-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {/* Left column: Field Type */}
            <div>
              <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide mb-4">
                {t("fieldType")}
              </h3>
              <div className="grid grid-cols-2 gap-3">
                {FIELD_TYPES.map(({ type, label, description, icon: Icon }) => (
                  <button
                    key={type}
                    type="button"
                    onClick={() => updateField("field_type", type)}
                    className={cn(
                      "flex flex-col items-start gap-2 rounded-md border p-4 text-left transition-all",
                      "hover:bg-accent/50 hover:shadow-sm",
                      form.field_type === type
                        ? "border-primary bg-primary/5 ring-2 ring-primary/20 shadow-sm"
                        : "border-border",
                    )}
                  >
                    <div className={cn(
                      "flex items-center justify-center h-10 w-10 rounded",
                      form.field_type === type
                        ? "bg-primary/10 text-primary"
                        : "bg-muted text-muted-foreground",
                    )}>
                      <Icon className="h-5 w-5" />
                    </div>
                    <div>
                      <p className="text-sm font-medium">{label[language]}</p>
                      <p className="text-xs text-muted-foreground mt-0.5">
                        {description[language]}
                      </p>
                    </div>
                  </button>
                ))}
              </div>
            </div>

            {/* Right column: Settings */}
            <div>
              <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide mb-4">
                {t("settings")}
              </h3>
              <div className="space-y-5">
                {/* Label */}
                <div className="space-y-2">
                  <Label htmlFor="field-label">{t("label")}</Label>
                  <Input
                    id="field-label"
                    placeholder={t("labelPlaceholder")}
                    value={form.label}
                    onChange={(e) => updateField("label", e.target.value)}
                    autoFocus
                  />
                </div>

                {/* Required toggle */}
                <div className="flex items-center justify-between rounded border p-4">
                  <div>
                    <Label htmlFor="field-required" className="text-sm font-medium">{t("required")}</Label>
                    <p className="text-xs text-muted-foreground mt-0.5">{t("requiredHelp")}</p>
                  </div>
                  <Switch
                    id="field-required"
                    checked={form.required === 1}
                    onCheckedChange={(checked) => updateField("required", checked ? 1 : 0)}
                  />
                </div>

                {/* Contextual settings */}
                {showMaxChars && (
                  <div className="space-y-2">
                    <Label htmlFor="field-max-chars">{t("maxChars")}</Label>
                    <Input
                      id="field-max-chars"
                      type="number"
                      min={0}
                      placeholder={t("noLimit")}
                      value={form.max_characters || ""}
                      onChange={(e) => updateField("max_characters", parseInt(e.target.value) || 0)}
                    />
                  </div>
                )}

                {showOptions && (
                  <div className="space-y-2">
                    <Label>{t("options")}</Label>
                    <div className="space-y-2">
                      {(form.options ? form.options.split("\n") : [""]).map((opt, i, arr) => {
                        const img = opt ? optionImages[opt] : undefined;
                        const isUploading = uploadingOpt === opt && !!opt;
                        return (
                        <div key={i} className="flex items-center gap-2">
                          {/* Per-option artwork thumbnail + upload (TKT-126) */}
                          <label
                            className={cn(
                              "relative flex h-9 w-9 shrink-0 cursor-pointer items-center justify-center overflow-hidden rounded-md border bg-muted/40",
                              !opt.trim() && "pointer-events-none opacity-40",
                            )}
                            title={t("optionImageTooltip")}
                          >
                            {isUploading ? (
                              <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
                            ) : img ? (
                              // eslint-disable-next-line @next/next/no-img-element
                              <img src={img} alt={opt} className="h-full w-full object-cover" />
                            ) : (
                              <ImagePlus className="h-4 w-4 text-muted-foreground" />
                            )}
                            <input
                              type="file"
                              accept="image/*"
                              className="hidden"
                              disabled={!opt.trim() || isUploading}
                              onChange={(e) => {
                                const f = e.target.files?.[0];
                                if (f) handleOptionImageUpload(opt, f);
                                e.target.value = "";
                              }}
                            />
                          </label>
                          <Input
                            placeholder={t("optionPlaceholder")}
                            value={opt}
                            onChange={(e) => {
                              const updated = [...arr];
                              const prevVal = updated[i];
                              updated[i] = e.target.value;
                              renameOptionImageKey(prevVal, e.target.value);
                              updateField("options", updated.join("\n"));
                            }}
                          />
                          {img && (
                            <Button
                              type="button"
                              variant="ghost"
                              size="icon"
                              className="h-9 w-9 shrink-0 text-muted-foreground"
                              onClick={() => setOptionImage(opt, null)}
                              aria-label={`Remove image for option ${i + 1}`}
                            >
                              <X className="h-4 w-4" />
                            </Button>
                          )}
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            className="h-9 w-9 shrink-0 text-destructive hover:text-destructive"
                            disabled={arr.length <= 1}
                            onClick={() => {
                              const updated = arr.filter((_, j) => j !== i);
                              setOptionImage(opt, null);
                              updateField("options", updated.join("\n"));
                            }}
                            aria-label={`Remove option ${i + 1}`}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                        );
                      })}
                    </div>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        const current = form.options ? form.options + "\n" : "";
                        updateField("options", current);
                      }}
                    >
                      <Plus className="h-4 w-4 mr-1" />
                      {t("addOption")}
                    </Button>
                  </div>
                )}

                {showFileSettings && (
                  <>
                    <div className="space-y-2">
                      <Label htmlFor="field-file-types">{t("allowedFileTypes")}</Label>
                      <Input
                        id="field-file-types"
                        placeholder="png,jpg,pdf"
                        value={form.allowed_file_types}
                        onChange={(e) => updateField("allowed_file_types", e.target.value)}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="field-max-size">{t("maxFileSize")}</Label>
                      <Input
                        id="field-max-size"
                        type="number"
                        min={1}
                        value={form.max_file_size || 5}
                        onChange={(e) => updateField("max_file_size", parseInt(e.target.value) || 5)}
                      />
                    </div>
                  </>
                )}

                {/* Add-on pricing */}
                <Collapsible open={addonOpen} onOpenChange={setAddonOpen}>
                  <CollapsibleTrigger className="flex w-full items-center justify-between rounded border px-4 py-3 text-sm font-medium hover:bg-accent/50 transition-colors">
                    {t("addonPricing")}
                    <ChevronDown className={cn("h-4 w-4 transition-transform", addonOpen && "rotate-180")} />
                  </CollapsibleTrigger>
                  <CollapsibleContent className="pt-3 space-y-4">
                    <div className="space-y-2">
                      <Label htmlFor="field-addon-price">{t("addonPrice")}</Label>
                      <Input
                        id="field-addon-price"
                        type="number"
                        min={0}
                        step="0.01"
                        placeholder="0"
                        value={form.addon_price || ""}
                        onChange={(e) => updateField("addon_price", parseFloat(e.target.value) || 0)}
                      />
                      <p className="text-xs text-muted-foreground">{t("addonPriceHelp")}</p>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="field-addon-item">{t("addonItemCode")}</Label>
                      <Input
                        id="field-addon-item"
                        placeholder={t("addonPlaceholder")}
                        value={form.addon_item}
                        onChange={(e) => updateField("addon_item", e.target.value)}
                      />
                      <p className="text-xs text-muted-foreground">{t("addonHelp")}</p>
                    </div>
                  </CollapsibleContent>
                </Collapsible>
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex justify-end gap-3 border-t px-8 py-5">
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            {t("cancel")}
          </Button>
          <Button onClick={handleSave} disabled={!form.label.trim()}>
            {isEdit ? t("saveChanges") : t("addField")}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
